import React from 'react'
import { FileText, ChevronDown } from 'lucide-react'
import Button from '../../../../components/ui/button'
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuLabel } from '../../../../components/ui/contextmenu'
import { usePodcastEditor } from '../../context/use-podcast-editor'
import { exportScriptToPdf } from '../../../../lib/export-script-pdf'
import { track } from '../../../../lib/analytics/posthog'

// Hook to get export handlers - used by both ExportButton and settings dialog
export const useExportHandlers = () => {
  const { podcast, voices, hostAVoiceId, hostBVoiceId } = usePodcastEditor()
  
  const hostAName = React.useMemo(() => {
    const voice = voices.find(v => v.id === hostAVoiceId)
    return voice?.name || 'Host A'
  }, [voices, hostAVoiceId])
  
  const hostBName = React.useMemo(() => {
    const voice = voices.find(v => v.id === hostBVoiceId)
    return voice?.name || 'Host B'
  }, [voices, hostBVoiceId])
  
  const hasLines = podcast?.podcastLines && podcast.podcastLines.length > 0

  const handleExportPdf = React.useCallback(() => {
    if (!podcast) return
    
    track('script_exported_pdf', { podcastId: podcast.id })
    
    exportScriptToPdf({
      podcast,
      hostAName,
      hostBName,
    })
  }, [podcast, hostAName, hostBName])

  return { hasLines, handleExportPdf }
}

// Standalone export section for mobile settings dialog
export const ExportSection: React.FC = () => {
  const { hasLines, handleExportPdf } = useExportHandlers()

  if (!hasLines) {
    return null
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-foreground/70">Export</h3>
      <button
        type="button"
        onClick={handleExportPdf}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-black/10 bg-white hover:bg-black/[0.02] transition-colors cursor-pointer text-left"
      >
        <FileText className="h-4 w-4 text-foreground/60" aria-hidden="true" />
        <span className="text-sm font-medium text-foreground">Export script as PDF</span>
      </button>
    </div>
  )
}

export const ExportButton: React.FC = () => {
  const { hasLines, handleExportPdf } = useExportHandlers()

  if (!hasLines) {
    return null
  }

  return (
    <ContextMenu>
      <ContextMenuContent>
        <ContextMenuLabel>Export options</ContextMenuLabel>
        <ContextMenuItem onClick={handleExportPdf}>
          <FileText className="h-4 w-4 text-foreground/60" aria-hidden="true" />
          Export script as PDF
        </ContextMenuItem>
      </ContextMenuContent>
      <ContextMenuTrigger asChild anchor="element">
        <Button variant="secondary" size="sm" className="gap-1.5">
          Export
          <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
        </Button>
      </ContextMenuTrigger>
    </ContextMenu>
  )
}
